/**
 * Карта родства экранов: откуда каждый экран вырос и куда обязана вести стрелка
 * «назад».
 *
 * ## Почему это лежит в `shared`, а не в `app`
 *
 * Карту читает [`shared/ui/page.tsx`](../ui/page.tsx) — каркас, который рисует
 * заголовок, крошки и стрелку на КАЖДОМ экране. Импорт в этом дереве идёт сверху
 * вниз, так что `shared` не имеет права заглянуть в `app`; а единственная
 * альтернатива — раздать те же отношения по тридцати экранам пропсами — это
 * тридцать мест, где они разъедутся. Файл поэтому лежит здесь и не импортирует
 * ничего вовсе: это таблица, а не поведение.
 *
 * Что она не разъехалась с адресами, проверяет тест рядом с
 * [`app/linking.ts`](../../app/linking.ts).
 *
 * ## Зачем она вообще нужна
 *
 * `navigation.goBack()` возвращает не туда, откуда экран ОТКРЫВАЕТСЯ по смыслу, а
 * туда, откуда на него зашли в этот раз. Пока в экран ведёт ровно одна дверь, разницы
 * нет; как только их две — «Настройки отдела» открываются и из списка отделов, и из
 * отчёта по этому же отделу, — стрелка начинает водить кругами: нажал «назад» в
 * настройках отдела и оказался в отчёте, из которого выйти можно только ещё одним
 * «назад» в совсем другой раздел.
 *
 * Поэтому «назад» здесь — это не «шаг назад по истории», а **«на уровень вверх по
 * приложению»**, и уровень назначается тут, один раз, рядом с адресом
 * ([`app/linking.ts`](../../app/linking.ts) описывает те же отношения путями:
 * `settings/departments/:deptId` — это ребёнок `settings/departments`, который
 * ребёнок `settings`).
 *
 * Из этой же карты берутся «хлебные крошки» и подпись у стрелки: «← Отделы» вместо
 * безымянного «← Назад». Человек, попавший на экран по ссылке, по закладке или
 * переключением персоны, читает с него, ГДЕ он находится, — а не выясняет это
 * нажатием.
 *
 * ## Правило, которое нельзя нарушить
 *
 * **Имя экрана в крошке обязано совпадать с надписью на строке, которая туда ведёт.**
 * Строка в «Настройках» говорит «Реквизиты компании» — крошка обязана говорить то же
 * самое, а не «Настройки компании». Две разные надписи на одну страницу — это две
 * страницы в голове читающего.
 */

/** Экран, к которому ведёт крошка или стрелка «назад». */
export type Crumb = {
  screen: string;
  params?: Record<string, unknown>;
  /**
   * Ключ словаря; `null` — имя берётся из параметра маршрута, а без него — из
   * `fallbackKey` («Сотрудник», «Устройство»), потому что по прямой ссылке на
   * `/staff/:id/edit` имя человека в адресе не едет, а «PersonnelCard» в крошке —
   * это внутреннее имя экрана, которое читателю ничего не говорит.
   */
  titleKey: string | null;
  fallbackKey?: string;
  /** Готовое имя, когда оно пришло параметром маршрута (человек, отдел, терминал). */
  title?: string;
};

type Params = Readonly<Record<string, unknown>> | undefined;

/**
 * Родитель: имя экрана, когда оно одно, или функция — когда родитель зависит от
 * параметров. Форма сотрудника открывается и как «новый» (из списка), и как «правка»
 * (из карточки), и вверх ведёт в разное.
 */
type Parent = string | ((params: Params) => {screen: string; params?: Record<string, unknown>} | undefined);

type ScreenNode = {
  /**
   * Как экран называется в крошках. `null` — экран носит имя своего предмета
   * (человека, отдела, терминала), и оно приезжает параметром `name`.
   */
  titleKey: string | null | ((params: Params) => string);
  /** Подпись, когда имени в параметрах нет. Только у экранов с `titleKey: null`. */
  fallbackKey?: string;
  /** На уровень вверх. Отсутствует — экран является корнем своего раздела. */
  parent?: Parent;
};

/** Имя предмета, приехавшее параметром маршрута. */
function nameOf(params: Params): string | undefined {
  const name = params?.name;
  return typeof name === 'string' && name.length > 0 ? name : undefined;
}

/**
 * Все экраны приложения и их родители.
 *
 * Корни разделов (`Overview`, `Journal`, `PersonnelList`, `ReportsOverview`,
 * `DeviceList`, `Settings`, …) родителя не имеют намеренно: над вкладкой ничего нет,
 * и стрелка «назад» на ней — это стрелка в никуда.
 */
export const SCREENS: Readonly<Record<string, ScreenNode>> = {
  // ── Вход ───────────────────────────────────────────────────────────────────
  Login: {titleKey: 'auth.login.title'},
  Register: {titleKey: 'auth.register.title', parent: 'Login'},
  VerifyEmail: {titleKey: 'auth.verify.title', parent: 'Register'},
  Forgot: {titleKey: 'auth.forgot.title', parent: 'Login'},

  // ── Главная ────────────────────────────────────────────────────────────────
  Overview: {titleKey: 'nav.overview'},

  // ── Журнал ─────────────────────────────────────────────────────────────────
  Journal: {titleKey: 'nav.journal'},

  // ── Люди (личное пространство) ─────────────────────────────────────────────
  PeopleList: {titleKey: 'nav.people'},
  Person: {titleKey: null, fallbackKey: 'crumbs.person', parent: 'PeopleList'},

  // ── Сотрудники (компания) ──────────────────────────────────────────────────
  PersonnelList: {titleKey: 'nav.staff'},
  PersonnelDetached: {titleKey: 'detached.title', parent: 'PersonnelList'},
  PersonnelCard: {titleKey: null, fallbackKey: 'crumbs.person', parent: 'PersonnelList'},
  PersonnelForm: {
    titleKey: params =>
      params?.recordId ? 'personnel.editTitle' : 'personnel.createTitle',
    // Правка растёт из карточки, создание — из списка. Стрелка ведёт туда, где
    // результат правки виден.
    parent: params =>
      params?.recordId
        ? {
            screen: 'PersonnelCard',
            params: {recordId: params.recordId, name: params.name},
          }
        : {screen: 'PersonnelList'},
  },

  // ── Посещаемость ───────────────────────────────────────────────────────────
  ReportsOverview: {titleKey: 'nav.attendance'},
  DepartmentReport: {titleKey: null, fallbackKey: 'crumbs.department', parent: 'ReportsOverview'},
  EmployeeReport: {titleKey: null, fallbackKey: 'crumbs.person', parent: 'ReportsOverview'},
  Payroll: {titleKey: 'payroll.title', parent: 'ReportsOverview'},

  // ── График работы ──────────────────────────────────────────────────────────
  Schedule: {titleKey: 'nav.schedule'},

  // ── Устройства ─────────────────────────────────────────────────────────────
  DeviceList: {titleKey: 'nav.devices'},
  Device: {titleKey: null, fallbackKey: 'crumbs.device', parent: 'DeviceList'},
  DeviceSettings: {
    titleKey: 'terminal.settings',
    parent: params => ({
      screen: 'Device',
      params: {deviceId: params?.deviceId, name: params?.name},
    }),
  },
  ScanDevice: {titleKey: 'home.adopt.scan.title', parent: 'DeviceList'},
  ConfirmAdopt: {titleKey: 'home.adopt.confirm.title', parent: 'ScanDevice'},

  // ── Камеры ─────────────────────────────────────────────────────────────────
  CameraWall: {titleKey: 'nav.cameras'},
  CameraSettings: {titleKey: 'camera.settingsTitle', parent: 'CameraWall'},

  // ── Персона «сотрудник» ────────────────────────────────────────────────────
  MyAttendance: {titleKey: 'nav.myAttendance'},
  MyLogs: {titleKey: 'nav.myLogs'},

  // ── Настройки ──────────────────────────────────────────────────────────────
  Settings: {titleKey: 'nav.settings'},
  Departments: {titleKey: 'nav.departments', parent: 'Settings'},
  Department: {titleKey: null, fallbackKey: 'crumbs.department', parent: 'Departments'},
  Members: {titleKey: 'members.title', parent: 'Settings'},
  Telegram: {titleKey: 'telegram.title', parent: 'Settings'},
  NotificationSettings: {titleKey: 'notifSettings.title', parent: 'Settings'},
  CompanySettings: {titleKey: 'settings.companyDetails', parent: 'Settings'},
  CalendarSettings: {titleKey: 'nav.calendar', parent: 'Settings'},
  TerminalProfiles: {titleKey: 'more.terminalProfiles', parent: 'Settings'},
  ApiBridge: {titleKey: 'more.apiBridge', parent: 'Settings'},
  Account: {titleKey: 'settings.account', parent: 'Settings'},
  Security: {titleKey: 'more.security', parent: 'Settings'},
  PushSettings: {titleKey: 'push.title', parent: 'Settings'},
};

/** Как называется экран: имя предмета, если оно есть, иначе ключ словаря. */
function crumbFor(screen: string, params: Params): Crumb {
  const node = SCREENS[screen];
  const key = typeof node?.titleKey === 'function' ? node.titleKey(params) : node?.titleKey;
  return {
    screen,
    params: params as Record<string, unknown> | undefined,
    titleKey: key ?? null,
    fallbackKey: node?.fallbackKey,
    title: nameOf(params),
  };
}

/** На уровень вверх от этого экрана. `undefined` — экран корневой. */
export function parentOf(screen: string, params: Params): Crumb | undefined {
  const parent = SCREENS[screen]?.parent;
  if (!parent) {
    return undefined;
  }
  const resolved = typeof parent === 'function' ? parent(params) : {screen: parent};
  if (!resolved || !SCREENS[resolved.screen]) {
    return undefined;
  }
  return crumbFor(resolved.screen, resolved.params);
}

/**
 * Предки экрана, от корня раздела к ближайшему родителю. Сам экран в цепочку не
 * входит: крошки рисуют путь ДО страницы, а как называется сама страница, написано
 * заголовком под ними.
 */
export function trailOf(screen: string, params: Params): Crumb[] {
  const trail: Crumb[] = [];
  let current = parentOf(screen, params);
  // Цикл в карте родства повесил бы приложение молча, поэтому глубина ограничена.
  for (let hops = 0; current && hops < 8; hops += 1) {
    trail.unshift(current);
    current = parentOf(current.screen, current.params);
  }
  return trail;
}

/**
 * Лежит ли `screen` где-то ПОД `ancestor` по этой карте.
 *
 * Нужно ровно для одного решения: стрелка «назад» имеет право сделать обычный шаг по
 * истории, только если предыдущий экран — это родитель или кто-то из его потомков.
 * Спустились «компания → отдел → сотрудник» — шаг назад законен и возвращает в отдел.
 * Приехали в настройки отдела из отчёта — предыдущий экран потомком не является, и
 * стрелка обязана уйти в список отделов, а не обратно в отчёт.
 */
export function isUnder(screen: string, ancestor: string): boolean {
  let current: string | undefined = screen;
  for (let hops = 0; current && hops < 8; hops += 1) {
    if (current === ancestor) {
      return true;
    }
    const parent: Parent | undefined = SCREENS[current]?.parent;
    current = typeof parent === 'function' ? parent(undefined)?.screen : parent;
  }
  return false;
}
